'use client'

/**
 * Utility function to check if the Indian stock market is currently open
 * Market hours are 9:30 AM to 3:30 PM IST (UTC+5:30), Monday to Friday
 */
export function isMarketOpen(): boolean {
  // Get current date and time in IST (UTC+5:30)
  const now = new Date();
  
  // Convert to IST by adding 5 hours and 30 minutes to UTC
  const istTime = new Date(now.getTime() + (5.5 * 60 * 60 * 1000));
  
  // Get day of week (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
  const dayOfWeek = istTime.getUTCDay();
  
  // Check if it's a weekday (Monday to Friday)
  if (dayOfWeek === 0 || dayOfWeek === 6) {
    return false; // Weekend
  }
  
  // Get hours and minutes in IST
  const hours = istTime.getUTCHours();
  const minutes = istTime.getUTCMinutes();
  
  // Convert to minutes since midnight for easier comparison
  const timeInMinutes = (hours * 60) + minutes;
  
  // Market opens at 9:30 AM IST (9*60 + 30 = 570 minutes)
  const marketOpenMinutes = 9 * 60 + 30;
  
  // Market closes at 3:30 PM IST (15*60 + 30 = 930 minutes)
  const marketCloseMinutes = 15 * 60 + 30;
  
  // Check if current time is within market hours
  return timeInMinutes >= marketOpenMinutes && timeInMinutes <= marketCloseMinutes;
}

/**
 * Get the next market open time in milliseconds
 * Returns the time until the market opens next
 */
export function getTimeUntilMarketOpens(): number {
  const now = new Date();
  
  // Convert to IST by adding 5 hours and 30 minutes to UTC
  const istTime = new Date(now.getTime() + (5.5 * 60 * 60 * 1000));
  
  // Get day of week (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
  let dayOfWeek = istTime.getUTCDay();
  
  // Get hours and minutes in IST
  const hours = istTime.getUTCHours();
  const minutes = istTime.getUTCMinutes();
  
  // Convert to minutes since midnight for easier comparison
  const timeInMinutes = (hours * 60) + minutes;
  
  // Market opens at 9:30 AM IST (9*60 + 30 = 570 minutes)
  const marketOpenMinutes = 9 * 60 + 30;
  
  // Calculate days until next market open
  let daysToAdd = 0;
  
  if (dayOfWeek === 0) { // Sunday
    daysToAdd = 1; // Next market day is Monday
  } else if (dayOfWeek === 6) { // Saturday
    daysToAdd = 2; // Next market day is Monday
  } else if (timeInMinutes >= marketOpenMinutes) { // Weekday after market open
    daysToAdd = 1; // Next market day is tomorrow
  }
  
  // Create date for next market open
  const nextMarketOpen = new Date(istTime.getTime());
  nextMarketOpen.setUTCDate(nextMarketOpen.getUTCDate() + daysToAdd);
  nextMarketOpen.setUTCHours(marketOpenMinutes / 60);
  nextMarketOpen.setUTCMinutes(marketOpenMinutes % 60);
  nextMarketOpen.setUTCSeconds(0);
  nextMarketOpen.setUTCMilliseconds(0);
  
  // Convert back from IST to local time
  const nextMarketOpenLocal = new Date(nextMarketOpen.getTime() - (5.5 * 60 * 60 * 1000));
  
  // Return milliseconds until next market open
  return nextMarketOpenLocal.getTime() - now.getTime();
}

/**
 * Format a time duration in milliseconds to a human-readable string
 */
export function formatTimeDuration(milliseconds: number): string {
  const seconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  
  if (days > 0) {
    return `${days}d ${hours % 24}h`;
  } else if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  } else {
    return `${seconds}s`;
  }
}